// src/admin/AdminOrderDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const STATUSES = ['pending', 'pending_confirmation', 'confirmed', 'prepared', 'shipped', 'delivered', 'cancelled'];
const STATUS_LABELS = {
  pending:   'En attente',
  pending_confirmation: 'À confirmer',
  confirmed: 'Confirmée',
  prepared:  'Préparée',
  shipped:   'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
};

const PAYMENT_LABELS = {
  cod:      'Paiement à la livraison',
  stripe:   'Carte bancaire (Stripe)',
  transfer: 'Virement bancaire',
  whatsapp: 'Finalisé via WhatsApp',
};

const getImageUrl = (image) => {
  if (!image || typeof image !== 'string') return '';
  const clean = image.trim();
  if (/^https?:\/\//i.test(clean)) return clean;
  const filename = clean.split('/').pop();
  if (!filename) return '';
  return `https://madadline.com/api/product-images/${encodeURIComponent(filename)}`;
};

function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    axios.get(`https://madadline.com/api/admin/orders/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => setOrder(data))
      .catch((err) => {
        console.error('Erreur chargement commande :', err.response?.status, err.response?.data || err);
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        setLoadError(`${status ? `[${status}] ` : ''}${msg}`);
        setOrder(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (newStatus) => {
    if (!window.confirm(`Changer le statut en "${STATUS_LABELS[newStatus]}" ?`)) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`https://madadline.com/api/orders/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrder(prev => ({ ...prev, status: newStatus }));
    } catch (e) {
      alert('Erreur lors de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  const togglePaid = async () => {
    const next = order.payment_status === 'paid' ? 'unpaid' : 'paid';
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`https://madadline.com/api/orders/${id}`, { payment_status: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrder(prev => ({ ...prev, payment_status: next }));
    } catch (e) {
      alert('Erreur lors de la mise à jour du paiement.');
    } finally {
      setSaving(false);
    }
  };

  const deleteOrder = async () => {
    if (!window.confirm(`Supprimer définitivement la commande #${id} ? Cette action est irréversible.`)) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`https://madadline.com/api/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/admin/commandes');
    } catch (e) {
      alert('Erreur lors de la suppression.');
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-loading"><div className="spinner" /></div>;
  if (!order) {
    return (
      <div className="admin-empty">
        <p>Commande introuvable.</p>
        {loadError && <p style={{ fontSize: 13, color: '#DC2626' }}>{loadError}</p>}
        <button onClick={() => navigate('/admin/commandes')} className="admin-btn">
          ← Retour
        </button>
      </div>
    );
  }

  const items = order.items || order.cart_items || [];

  /* ⭐ Infos du CHECKOUT en priorité, compte (token) en secours */
  const customerName  = order.customer_name || order.user?.name || '—';
  const customerPhone = order.customer_phone || order.user?.phone || '';
  const isGuest       = !order.user_id || Boolean(order.user?.is_guest);
  const waNumber      = customerPhone.replace(/\D/g, '');

  /* Montants : le prix de chaque ligne est celui enregistré à la commande */
  const itemPrice = (it) => parseFloat(it.price ?? it.prix ?? 0);
  const subTotal  = items.reduce((acc, it) => acc + itemPrice(it) * Number(it.quantity || 0), 0);
  const shipping  = parseFloat(order.shipping_fee || 0);

  const waMessage =
    `Bonjour ${customerName}, concernant votre commande #${order.id} chez MADAD...`;

  return (
    <div className="admin-order-detail">

      <header className="admin-page-head">
        <div>
          <h1>Commande #{order.id}</h1>
          <p>{new Date(order.created_at).toLocaleString('fr-FR')}</p>
        </div>
        <button onClick={() => navigate('/admin/commandes')} className="admin-btn">
          ← Retour
        </button>
      </header>

      {/* Statut */}
      <div className="admin-card">
        <div className="admin-card__head">
          <h2>Statut de la commande</h2>
          <span className={`admin-badge admin-badge--${order.status || 'pending'}`}>
            {STATUS_LABELS[order.status] || order.status}
          </span>
        </div>
        <div className="admin-status-actions">
          {STATUSES.map(s => (
            <button
              key={s}
              className={`admin-chip ${order.status === s ? 'is-active' : ''}`}
              onClick={() => updateStatus(s)}
              disabled={saving || order.status === s}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Cliente / Livraison / Paiement */}
      <div className="admin-grid-2">
        <div className="admin-card">
          <div className="admin-card__head">
            <h2>Cliente</h2>
            <span
              className="admin-badge admin-badge--neutral"
              style={{ color: isGuest ? '#B45309' : '#16A34A' }}
            >
              {isGuest ? 'Invitée (sans compte)' : 'Compte client'}
            </span>
          </div>
          <p><strong>Nom complet :</strong> {customerName}</p>
          <p>
            <strong>Téléphone :</strong>{' '}
            {customerPhone ? (
              <>
                <a href={`tel:${customerPhone}`}>{customerPhone}</a>
              </>
            ) : '—'}
          </p>
          {!isGuest && (
            <>
              <p><strong>Compte :</strong> {order.user?.name || '—'}</p>
              <p><strong>Email du compte :</strong> {order.user?.email || '—'}</p>
            </>
          )}
        </div>

        <div className="admin-card">
          <div className="admin-card__head"><h2>Livraison</h2></div>
          <p><strong>Ville :</strong> {order.city || '—'}</p>
          <p><strong>Adresse :</strong> {order.address || '—'}</p>
          <p><strong>Pays :</strong> {order.country || 'Maroc'}</p>
          <p><strong>Note de la cliente :</strong> {order.note || '—'}</p>
          {order.special_instructions && (
            <p><strong>Instructions spéciales :</strong> {order.special_instructions}</p>
          )}
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card__head"><h2>Paiement</h2></div>
        <p>
          <strong>Mode :</strong>{' '}
          {PAYMENT_LABELS[order.payment_method] || order.payment_method || '—'}
        </p>
        <p>
          <strong>État :</strong>{' '}
          <span style={{ color: order.payment_status === 'paid' ? '#16A34A' : '#B45309', fontWeight: 600 }}>
            {order.payment_status === 'paid' ? 'Payée' : 'Non payée'}
          </span>
        </p>
        <button className="admin-btn" onClick={togglePaid} disabled={saving}>
          {order.payment_status === 'paid' ? 'Marquer comme non payée' : '✓ Marquer comme payée'}
        </button>
      </div>

      {/* Articles */}
      <div className="admin-card" style={{ overflowX: 'auto' }}>
        <div className="admin-card__head">
          <h2>Articles à préparer</h2>
          <span className="admin-badge admin-badge--neutral">{items.length} article(s)</span>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Produit</th>
              <th>Taille</th>
              <th>Couleur</th>
              <th>Matière</th>
              <th>Qté</th>
              <th>Prix unit.</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const img = getImageUrl(it.product?.image);
              const name = it.product?.nom || it.product?.name || it.nom || 'Produit supprimé';
              return (
                <tr key={it.id || i}>
                  <td>
                    <div className="admin-table__user">
                      {img && (
                        <img
                          src={img}
                          alt=""
                          style={{ width: 44, height: 56, objectFit: 'cover', borderRadius: 4 }}
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      )}
                      <span>{name}</span>
                    </div>
                  </td>
                  <td>{it.size || '—'}</td>
                  <td>{it.color || '—'}</td>
                  <td>{it.material || '—'}</td>
                  <td>{it.quantity}</td>
                  <td>{itemPrice(it).toFixed(0)} DH</td>
                  <td><strong>{(itemPrice(it) * Number(it.quantity || 0)).toFixed(0)} DH</strong></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="6" style={{ textAlign: 'right' }}>Sous-total</td>
              <td>{subTotal.toFixed(0)} DH</td>
            </tr>
            <tr>
              <td colSpan="6" style={{ textAlign: 'right' }}>Livraison</td>
              <td>{shipping.toFixed(0)} DH</td>
            </tr>
            <tr>
              <td colSpan="6" style={{ textAlign: 'right' }}><strong>Total</strong></td>
              <td><strong>{parseFloat(order.total || 0).toFixed(0)} DH</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Actions */}
      <div className="admin-order-actions">
        {waNumber && (
          <a
            href={`https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`}
            target="_blank"
            rel="noreferrer"
            className="admin-btn admin-btn--success"
          >
            📱 Contacter sur WhatsApp
          </a>
        )}
        <button
          className="admin-btn"
          onClick={deleteOrder}
          disabled={saving}
          style={{ color: '#DC2626', borderColor: '#DC2626' }}
        >
          🗑 Supprimer la commande
        </button>
      </div>
    </div>
  );
}

export default AdminOrderDetail;